/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */

import React, { useCallback, useMemo } from 'react';
import Select from '../../../../../components/form/elements/select';
import { FormComponent } from '../../../../../components/form';
import { map } from 'rxjs/operators';
import { useParams } from 'react-router-dom';
import { useService } from '../../../../../services/services';
import { useTranslation } from 'react-i18next';
import Disabled from '../../../../../components/form/elements/disabled';
import RadioGroup from '../../../../../components/form/elements/radio-group';
import AnimalType from '../../../../../enums/animal-type';
import Watch from '../../../../../components/form/elements/watch';
import { FormMode } from '../../../../../enums/form-mode';
import Animal from '../../../../../interfaces/animal';
import { useQuery } from '../../../../../hooks/use-query';
import AnimalGenders from '../../../../../enums/animal-genders';
import { AnimalEventAnimal } from '../../../../../interfaces/animal-event';

interface Props {
  addRow: (a: any, b?: any) => void;
  setEventType?: (value: any) => void;
  setAnimals?: (value: any) => void;
  animals: Animal[];
  showFilters: boolean;
}

function useFormTop({
  addRow,
  setEventType,
  setAnimals,
  animals,
  showFilters,
}: Props) {
  const { farmId, id, mode } = useParams<{
    farmId: string;
    id: string;
    mode: string;
  }>();
  const { t } = useTranslation();
  const [params] = useQuery();
  const animalIds: string[] | null = params['animals']
    ? ([...new Set(params['animals'].split(','))] as string[])
    : null;

  const transKeyPrefix = 'pages.livestock.animals_and_events.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { animalService, barnService } = useService();

  const updateSearchParams = (searchParams, getValues) => {
    if (getValues('event_type') === AnimalType.dry_off) {
      searchParams.gender = AnimalGenders.FEMALE;
    } else {
      let birth_date = new Date();
      birth_date.setFullYear(new Date().getFullYear() - 1);
      searchParams.born_after = birth_date.toLocaleDateString('en-CA');
    }
  }

  const top = useMemo<FormComponent[]>(() => {
    const topFilterElements = [
      Select('species', {
        asyncOptions: (value, _loadedOptions, { page, setValue, getValues }) => {
          let searchParams = { value };

          updateSearchParams(searchParams, getValues);

          return animalService.species(farmId, searchParams).pipe(
            map((response) => {
              if (response.length === 1) {
                setValue('species', response[0]);
              }

              return {
                hasMore: false,
                options: response,
                additional: { page },
              };
            })
          );
        },
        label: tp('animals.species'),
        column: true,
        clearable: false,
        cacheOn: ['event_type'],
      }),
      Select('barn_ldc_nr', {
        asyncOptions: (_value, _loadedOptions, { setValue, getValues }) => {
          let searchParams = {
            add_location_to_label: true,
          };

          updateSearchParams(searchParams, getValues);

          return animalService
            .barns(farmId, searchParams)
            .pipe(
              map((barns) => {
                if (barns.length === 1) {
                  setValue('barn_ldc_nr', barns[0]);
                }

                return {
                  hasMore: false,
                  options: barns,
                  additional: false,
                };
              })
            );
        },
        label: tp('animals.barn'),
        column: true,
        clearable: false,
        cacheOn: ['event_type']
      }),
      Disabled(
        Watch(
          Select('animal_id', {
            asyncOptions: (value, _loadedOptions, { page, getValues }) => {
              const barn_ldc_nr = getValues('barn_ldc_nr')?.value;
              const species = getValues('species')?.value;
              const isPregnancy =
                getValues('event_type') === AnimalType.dry_off;
              const alreadyAddedAnimalIds = getValues("animals").map(
                (animal: AnimalEventAnimal) => animal.id
              );

              return animalService
                .index(farmId, {
                  page,
                  barn_ldc_nr,
                  species,
                  gender: isPregnancy ? AnimalGenders.FEMALE : '',
                  animal_id_last4: value,
                })
                .pipe(
                  map((response) => {
                    const filteredOptions = response.data.filter(
                      (animal) => !alreadyAddedAnimalIds.includes(animal.id)
                    );

                    return {
                      hasMore: response.last_page > response.current_page,
                      options: filteredOptions
                        .filter((animal) => {
                          if (isPregnancy) return true;
                          else {
                            const animalAge = animal.age.split(' ')[0];
                            if (Number(animalAge) < 1) return true;
                            return false;
                          }
                        })
                        .map((animal) => {
                          const birthDayDate: any = new Date(animal.birth_date);
                          const today: any = new Date();
                          const daysFromBirth = Math.round(
                            (today - birthDayDate) / (1000 * 3600 * 24)
                          );

                          return {
                            label: animal.animal_id,
                            value: animal.id,
                            ...animal,
                            age_of_days: daysFromBirth,
                          };
                        }),
                      additional: { page: response.current_page + 1 },
                    };
                  })
                );
            },
            label: tp('events.grazing.add_animal'),
            cacheOn: ['species.value', 'barn_ldc_nr.value', 'event_type', 'animals'],
            column: true,
          }),
          ({ value, setValue, getValues }) => {
            if (value.id) {
              const animal: Animal = { ...value };
              if (animals.findIndex((ittem) => ittem.id === animal.id) < 0) {
                const sumAgeOFdays = animals.reduce(
                  (accumulator, currentValue) =>
                    accumulator + Number(currentValue.age_of_days),
                  Number(animal.age_of_days)
                );
                const recordedWeight = getValues('recorded_weight');
                const birthWeight = getValues('birth_weight');

                const growth = Math.floor(
                  ((Number(recordedWeight) - Number(birthWeight)) * 1000) /
                    sumAgeOFdays
                );
                setValue('growth', growth);
                addRow(animal);
              }
            }
          }
        ),
        {
          watchOn: ['species', 'barn_ldc_nr', 'animal_id'],
          callback: (watchValues) => {
            const species = watchValues[0]?.value;
            const barn_ldc_nr = watchValues[1]?.label;
            return !(species && barn_ldc_nr);
          },
        }
      ),
    ];
    const elements = [
      Disabled(
        Watch(
          RadioGroup(
            'event_type',
            [
              {
                label: tp('animals.types.dry_off'),
                value: AnimalType.dry_off,
              },
              {
                label: tp('animals.types.weaning'),
                value: AnimalType.weaning,
              },
            ],
            tp('events.separation.event_type'),
            true
          ),
          ({ value }) => {
            if (setAnimals && animalIds) {
              let searchParams = {
                id: animalIds,
                gender: '',
                born_after: ''
              };

              if (value === AnimalType.dry_off) {
                searchParams.gender = AnimalGenders.FEMALE;
              } else {
                let birth_date = new Date();
                birth_date.setFullYear(new Date().getFullYear() - 1);
                searchParams.born_after = birth_date.toLocaleDateString('en-CA');
              }
              animalService
                .indexPost(farmId, searchParams,true)
                .subscribe((response) => {
                  setAnimals(
                    response.data.map((animal) => ({
                      animal_event_animal_id: null,
                      animal_id: animal.animal_id,
                      breed_code: animal.breed_code,
                      id: animal.id,
                      name: animal.name,
                      gender: animal.gender,
                      age: animal.age,
                      mother_name: animal.mother ? animal.mother.name : null,
                      mother_id: animal.mother ? animal.mother.animal_id : null,
                      age_of_days: animal.age_of_days ? animal.age_of_days : 0,
                    }))
                  )
                })
            }
            setEventType(value);
          }
        ),
        {
          watchOn: 'sub_type',
          callback: () => !!id && mode !== FormMode.view,
        }
      ),
    ];

    if (showFilters) {
      elements.splice(1, 0, ...topFilterElements);
    }

    return elements;
  }, []);
  return top;
}

export default useFormTop;
